/**
 * The client beacon.
 *
 * Hand-written rather than a third-party SDK for three reasons: it keeps the
 * bundle inside the < 60 KB budget (analytics SDKs start around 50 KB on their
 * own), it means no ad-tech tracker and therefore no consent-banner
 * complication, and the raw data stays entirely ours.
 *
 * Design constraints:
 *  - never block the UI, never throw into the app
 *  - never lose the last batch when the page is closed
 *  - never send anything that identifies a person
 *
 * Target size is about 1 KB minified and gzipped.
 */

import { SCHEMA_VERSION, LIMITS, type Batch, type EventName, type WireEvent } from './events.ts';

export interface BeaconConfig {
  /** Ingest endpoint, e.g. "/e". */
  readonly endpoint: string;
  readonly appVersion: string;
  /** Coefficient set the client is running; travels with every event. */
  coefficientVersion: string;
  /** Flush after this many events. */
  readonly batchSize?: number;
  /** Flush at least this often, in milliseconds. */
  readonly flushIntervalMs?: number;
  /** Set false to disable collection entirely (used by tests and by opt-out). */
  readonly enabled?: boolean;
}

/** Regenerate the session id after this much inactivity. */
const SESSION_IDLE_MS = 30 * 60 * 1000;

const DEVICE_KEY = 'p11.did';
const SESSION_KEY = 'p11.sid';
const SESSION_SEEN_KEY = 'p11.seen';

/** Opaque random id. No fingerprinting, nothing derived from the device. */
function randomId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += b.toString(36).padStart(2, '0');
  return out.slice(0, LIMITS.maxIdLength);
}

/** Storage can throw in private mode or when disabled; never let that reach the app. */
function safeGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function deviceClass(): string {
  const w = window.innerWidth;
  return w < 768 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
}

function connectionType(): string {
  const c = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
  return c?.effectiveType ?? 'unknown';
}

export interface Beacon {
  track(name: EventName, props?: Record<string, string | number | boolean>): void;
  /** Sends anything buffered. Called automatically on hide and unload. */
  flush(): void;
  /** True the first time this device was ever seen. */
  readonly isFirstOpen: boolean;
  readonly deviceId: string;
  readonly sessionId: string;
}

export function createBeacon(config: BeaconConfig): Beacon {
  const batchSize = config.batchSize ?? 20;
  const flushIntervalMs = config.flushIntervalMs ?? 15_000;
  const enabled = config.enabled ?? true;

  let deviceId = safeGet(localStorage, DEVICE_KEY) ?? '';
  const isFirstOpen = deviceId === '';
  if (isFirstOpen) {
    deviceId = randomId();
    safeSet(localStorage, DEVICE_KEY, deviceId);
  }

  // A session survives a reload but not half an hour away, so that "session
  // length" means a sitting rather than a browser tab's lifetime.
  const lastSeen = Number(safeGet(localStorage, SESSION_SEEN_KEY) ?? 0);
  let sessionId = safeGet(sessionStorage, SESSION_KEY) ?? '';
  if (sessionId === '' || Date.now() - lastSeen > SESSION_IDLE_MS) {
    sessionId = randomId();
    safeSet(sessionStorage, SESSION_KEY, sessionId);
  }

  let buffer: WireEvent[] = [];
  let batchStart = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const send = (): void => {
    if (buffer.length === 0) return;
    const batch: Batch = {
      v: SCHEMA_VERSION,
      d: deviceId,
      s: sessionId,
      a: config.appVersion,
      c: config.coefficientVersion,
      dc: deviceClass(),
      ct: connectionType(),
      e: buffer,
    };
    buffer = [];

    try {
      const body = JSON.stringify(batch);
      // sendBeacon survives the page being closed, which is the only way to
      // reliably capture session_end and the last events of a visit.
      if (!navigator.sendBeacon?.(config.endpoint, body)) {
        void fetch(config.endpoint, { method: 'POST', body, keepalive: true }).catch(() => {});
      }
    } catch {
      /* analytics must never break the app */
    }
  };

  const schedule = (): void => {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      send();
    }, flushIntervalMs);
  };

  const track: Beacon['track'] = (name, props) => {
    if (!enabled) return;
    try {
      const now = Date.now();
      if (buffer.length === 0) batchStart = now;
      buffer.push({ n: name, t: now - batchStart, p: props });
      safeSet(localStorage, SESSION_SEEN_KEY, String(now));
      if (buffer.length >= batchSize) send();
      else schedule();
    } catch {
      /* never throw into the app */
    }
  };

  if (enabled && typeof document !== 'undefined') {
    // `visibilitychange` is the only lifecycle event that fires reliably on
    // mobile: iOS Safari frequently skips `unload` and `beforeunload` entirely,
    // so relying on those loses the end of most phone sessions.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') send();
    });
    window.addEventListener('pagehide', send);
  }

  return {
    track,
    flush: send,
    isFirstOpen,
    deviceId,
    sessionId,
  };
}
